import {Directive, ElementRef, HostListener, inject, input} from '@angular/core';
import {ALLOWED_CTRL_CODES, ALLOWED_KEYS} from "../../core/constants/decimal-input.constants";

@Directive({
  selector: '[appDecimalLimit]'
})
export class DecimalLimitDirective {
  decimals = input<number>(0);

  private el: ElementRef<HTMLInputElement> = inject(ElementRef<HTMLInputElement>);

  private isControlKey(event: KeyboardEvent): boolean {
    return ALLOWED_KEYS.includes(event.key) ||
      ((event.ctrlKey || event.metaKey) && ALLOWED_CTRL_CODES.includes(event.code));
  }

  private isDecimalSeparator(key: string, currentValue: string): boolean {
    return (key === '.' || key === ',') &&
      !currentValue.includes('.') &&
      this.decimals() > 0;
  }

  private setSelectionIfSupported(input: HTMLInputElement, position: number): void {
    if (input.type !== 'number') {
      input.setSelectionRange(position, position);
    }
  }

  private validateAndFormat(value: string): string {
    const cleanedValue = value
      .replace(/,/g, '.')
      .replace(/[^0-9.]/g, '');
    const firstDotIndex = cleanedValue.indexOf('.');

    if (firstDotIndex === -1) { return cleanedValue; }

    const beforeDot = cleanedValue.slice(0, firstDotIndex);
    const afterDot = cleanedValue.slice(firstDotIndex + 1).replace(/\./g, '');
    const limitedDecimal = this.decimals() > 0 ?
      afterDot.slice(0, this.decimals()) :
      '';

    return beforeDot + '.' + limitedDecimal;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = this.el.nativeElement;
    const cursorPosition = input.selectionStart || 0;
    const oldValue = input.value;
    const value = this.validateAndFormat(oldValue);
    if (input.value === value) { return; }

    const lengthDiff = oldValue.length - value.length;
    input.value = value;
    const newCursorPosition = Math.max(0, Math.min(cursorPosition - lengthDiff, value.length));
    this.setSelectionIfSupported(input, newCursorPosition);
  }

  @HostListener('change', ['$event'])
  @HostListener('blur', ['$event'])
  onChange(): void {
    const input = this.el.nativeElement;
    const value = this.validateAndFormat(input.value);

    if (input.value !== value) {
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = this.el.nativeElement;
    const key = event.key;

    if (this.isControlKey(event)) { return; }
    if (/^\d$/.test(key)) { return; }
    if (this.isDecimalSeparator(key, input.value)) { return; }

    event.preventDefault();
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const input = this.el.nativeElement;
    const clipboardData = event.clipboardData?.getData('text') || '';
    if (!clipboardData) { return; }

    const cursorPosition = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    const currentValue = input.value;
    const beforeSelection = currentValue.slice(0, cursorPosition);
    const afterSelection = currentValue.slice(selectionEnd);
    const combinedValue = beforeSelection + clipboardData + afterSelection;
    const newValue = this.validateAndFormat(combinedValue);

    input.value = newValue;
    const newCursorPosition = newValue.length;
    this.setSelectionIfSupported(input, newCursorPosition);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
